import apiClient from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { guidOrNull } from "../../../utils/guidUtils";
import { getInternalActiveStaffLookup } from "../../../services/lookupService";
import {
  fetchGateAdvanceOverview,
  fetchStudyGate,
  gateNameForCode,
} from "../isf/isfStudyGateService";
import {
  getStudyRowId,
  getStudyScheduleId,
  getStudyStatusLabel,
} from "./studyMgmtReportUtils";

/** SP / DB archive status — must be exactly "Archived" (not "Archive"). */
export const STUDY_STATUS_ARCHIVED = "Archived";
export const STUDY_STATUS_LIVE = "Live";
/** Fallback when org setup has no retention configured yet. */
export const DEFAULT_ARCHIVE_RETENTION_YEARS = 10;

const idUpper = (v) =>
  v === undefined || v === null || v === "" ? "" : String(v).toUpperCase();

const pick = (obj, ...keys) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
      return obj[key];
    }
  }
  return undefined;
};

const unwrapActivityPayload = (res) => {
  const data = res?.data ?? res?.Data ?? res;
  if (!data || typeof data !== "object") return data;
  if (data.Tables && !data.StudyInfo && !data.StudyNumber) {
    const tables = Array.isArray(data.Tables) ? data.Tables : [];
    for (const table of tables) {
      const rows = table?.Rows || table?.rows;
      if (Array.isArray(rows) && rows[0]) return { ...data, ...rows[0] };
    }
  }
  return data;
};

const getFirstStudyInfo = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const raw = pick(payload, "StudyInfo", "studyInfo", "Study", "study");
  if (Array.isArray(raw) && raw[0]) return raw[0];
  if (raw && typeof raw === "object") return raw;
  if (
    pick(payload, "StudyNumber", "studyNumber") != null ||
    pick(payload, "StudyId", "studyId") != null ||
    pick(payload, "StudyName", "studyName") != null
  ) {
    return payload;
  }
  return null;
};

const parseMappedSiteIds = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((id) => String(id).trim()).filter(Boolean);
  }
  const text = String(raw || "").trim();
  if (!text) return [];
  return text
    .split(/[,;|]/)
    .map((part) => part.trim())
    .filter(Boolean);
};

/** Digits-only study number for usp_StudyActivity_NEWUI (@StudyNum → int). */
function resolveStudyNum(info, studyRow) {
  const raw =
    pick(info, "StudyNumber", "studyNumber", "StudyNum", "studyNum") ??
    studyRow?.StudyNumber ??
    studyRow?.StudyNum ??
    studyRow?.StudyCode ??
    "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  // Normalize leading zeros so Convert(int) matches TrialStudy.StudyNumber
  return String(parseInt(digits, 10));
}

const extractAppointmentRows = (response) => {
  const data = response?.data;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  const named =
    data.AppointmentList ||
    data.appointmentList ||
    data.SuccessStory ||
    data.Table ||
    data.table;
  if (Array.isArray(named)) return named;
  if (Array.isArray(data.Tables)) {
    for (const table of data.Tables) {
      const rows = table?.Rows || table?.rows;
      if (Array.isArray(rows) && rows.length) return rows;
    }
  }
  for (const key of Object.keys(data)) {
    if (key === "Tables") continue;
    const value = data[key];
    if (
      Array.isArray(value) &&
      value.length &&
      typeof value[0] === "object" &&
      (value[0].AppointmentStatus != null ||
        value[0].appointmentStatus != null ||
        value[0].AppointmentId != null)
    ) {
      return value;
    }
  }
  return [];
};

const normalizeAppointmentRow = (row) => ({
  id: String(
    row?.AppointmentId ?? row?.appointmentId ?? row?.id ?? Math.random(),
  ),
  volunteerName: String(row?.VolName ?? row?.VolunteerName ?? row?.volName ?? "—"),
  visitName: String(row?.VisitName ?? row?.visitName ?? "—"),
  startTime: String(row?.StartTime ?? row?.BookedDate ?? row?.startTime ?? "—"),
  status: String(row?.AppointmentStatus ?? row?.appointmentStatus ?? ""),
  appointmentType: String(row?.AppointmentType ?? row?.appointmentType ?? ""),
  locationName: String(row?.LocationName ?? row?.locationName ?? ""),
});

/**
 * Confirmed appointments for a study schedule (EPlan_Appointment.StudyIdRef = StudyScheduleId).
 */
export async function fetchConfirmedAppointmentsForStudy({
  userData,
  studyScheduleId,
}) {
  const scheduleId = guidOrNull(studyScheduleId);
  if (!scheduleId || !userData?.OrganizationId) {
    return { appointments: [], count: 0 };
  }

  const response = await apiClient.post(
    API_ENDPOINTS.EPLANNER.GET_APPOINTMENT,
    {
      OrganizationId: idUpper(userData.OrganizationId),
      CreatedBy: idUpper(userData.StaffId),
      StudyId: scheduleId,
      ActionType: "AppointmentList",
      AppointmentStatus: "Confirmed",
      AppointmentPeriod: "",
      StartPeriod: "",
      EndPeriod: "",
      VolunteerId: "",
      AppointmentType: null,
      StudyIds: null,
      LocationIds: null,
      VisitId: "",
      OrderBy: "",
      SortBy: "",
      GroupId: "",
    },
  );

  const rows = extractAppointmentRows(response)
    .map(normalizeAppointmentRow)
    .filter((row) => String(row.status).toLowerCase() === "confirmed");

  return { appointments: rows, count: rows.length, response };
}

const GUID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function looksLikeGuid(value) {
  return GUID_LIKE.test(String(value || "").trim());
}

function buildSiteNameLookup(siteIds = []) {
  const map = new Map();
  (siteIds || []).forEach((site) => {
    const id = String(
      site?.siteId ?? site?.SiteId ?? site?.value ?? site ?? "",
    )
      .trim()
      .toLowerCase();
    if (!id) return;
    const name = String(
      site?.name ??
        site?.SiteName ??
        site?.siteName ??
        site?.label ??
        site?.SiteAbbr ??
        "",
    ).trim();
    if (name && !looksLikeGuid(name)) map.set(id, name);
  });
  return map;
}

async function fetchIsfPsfPendingBundle({
  userData,
  studyId,
  siteIds,
  templateCategory,
}) {
  const gateResult = await fetchStudyGate({ userData, studyId }).catch(() => null);
  const gateCode =
    gateResult?.state?.CurrentGateCode ||
    gateResult?.state?.currentGateCode ||
    "PRE_STUDY";
  const gateLabel = gateNameForCode(gateCode);
  const siteNameById = buildSiteNameLookup(siteIds);

  const overview = await fetchGateAdvanceOverview({
    userData,
    studyId,
    gateCode,
    siteIds,
    templateCategory,
  }).catch(() => ({ kpis: {}, tasks: [] }));

  const tasks = (overview?.tasks || []).map((task) => {
    const subtitle = String(task?.subtitle || "").trim();
    // Never surface raw GUIDs in task subtitles
    if (looksLikeGuid(subtitle)) {
      const resolved = siteNameById.get(subtitle.toLowerCase());
      return {
        ...task,
        subtitle: resolved || "",
      };
    }
    return task;
  });

  return {
    category: templateCategory,
    gateCode,
    gateLabel,
    kpis: overview?.kpis || {},
    tasks,
    pendingSites: [],
    pendingCount: tasks.length,
    // Only real category open items (uploads/signatures) — never study-wide
    // gate-site readiness, which is not eISF/ePSF specific and caused false
    // "Site not ready · 1 pending" rows with no document list.
    isClear: tasks.length === 0,
  };
}

/**
 * Organisation archive retention (years) from EPlan_CalenderDefaultSetup.
 */
export async function fetchOrgArchiveRetentionYears(userData) {
  if (!userData?.OrganizationId || !userData?.StaffId) {
    return DEFAULT_ARCHIVE_RETENTION_YEARS;
  }
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.ORGANIZATION.SETUP_ACTIVITY,
      {
        ActionType: "GetSetup",
        OrganizationId: userData.OrganizationId,
        CreatedBy: userData.StaffId,
      },
    );
    const data = response?.data ?? response?.Data ?? response;
    const row = Array.isArray(data?.SuccessStory)
      ? data.SuccessStory[0]
      : data?.SuccessStory || null;
    const years = Number(
      row?.RetentionPeriodYears ?? row?.retentionPeriodYears,
    );
    if (Number.isFinite(years) && years >= 1) return Math.min(years, 100);
  } catch {
    /* default below */
  }
  return DEFAULT_ARCHIVE_RETENTION_YEARS;
}

/**
 * Load GetStudyInfo + build a full StudyActivity Update payload with Status=Archived.
 */
export async function loadStudyForArchive({ userData, studyId, studyRow }) {
  const resolvedStudyId = guidOrNull(studyId || getStudyRowId(studyRow));
  if (!resolvedStudyId || !userData?.OrganizationId) {
    throw new Error("Missing study or organization for archive.");
  }

  const response = await apiClient.post(API_ENDPOINTS.STUDY.STUDY_ACTIVITY, {
    OrganizationId: idUpper(userData.OrganizationId),
    CreatedBy: idUpper(userData.StaffId),
    StaffIdRef: idUpper(userData.StaffId),
    StudyId: idUpper(resolvedStudyId),
    Action: "GetStudyInfo",
  });

  const data = unwrapActivityPayload(response);
  const info = getFirstStudyInfo(data) || studyRow || {};

  const studyName =
    pick(info, "StudyName", "studyName") ||
    studyRow?.StudyName ||
    "";
  const studyNum = resolveStudyNum(info, studyRow);
  if (!studyNum) {
    throw new Error(
      "Could not resolve the study number required to archive. Refresh and try again.",
    );
  }
  const pi = idUpper(
    pick(
      info,
      "PrincipalInvestigator",
      "principalInvestigator",
      "PI",
      "pi",
    ) || studyRow?.PrincipalInvestigator || "",
  );
  if (!pi) {
    throw new Error(
      "This study has no Principal Investigator on file. Assign a PI, then archive.",
    );
  }
  const studyColor =
    pick(info, "StudyColor", "studyColor", "StudySchColor") ||
    studyRow?.StudyColor ||
    studyRow?.StudySchColor ||
    "";

  const looksLikeGuid = (value) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      String(value || "").trim(),
    );

  // API stores Department as DepartmentId (GUID). Keep ID for Update payload;
  // resolve a display name for the summary panel.
  const departmentRaw = String(
    pick(info, "Department", "department", "DepartmentId") ??
      studyRow?.Department ??
      studyRow?.DepartmentId ??
      "",
  ).trim();
  const departmentNameFromApi = String(
    pick(info, "DepartmentName", "departmentName") ??
      studyRow?.DepartmentName ??
      "",
  ).trim();
  let departmentId = departmentRaw;
  let departmentDisplay = "";

  if (departmentNameFromApi && !looksLikeGuid(departmentNameFromApi)) {
    departmentDisplay = departmentNameFromApi;
    if (!departmentId || !looksLikeGuid(departmentId)) {
      departmentId = departmentRaw;
    }
  } else if (departmentRaw && !looksLikeGuid(departmentRaw)) {
    departmentDisplay = departmentRaw;
  } else if (departmentRaw && looksLikeGuid(departmentRaw)) {
    departmentId = departmentRaw;
    try {
      const commonRes = await apiClient.post(
        API_ENDPOINTS.USER.GET_USER_PROFILE_COMMONDATA,
        {
          OrganizationId: userData.OrganizationId,
          StaffIDRef: userData.StaffId,
          CreatedBy: userData.StaffId,
          PageName: "AddStudyReact",
        },
      );
      const commonPayload = commonRes?.data ?? commonRes?.Data ?? commonRes;
      const deptRows =
        commonPayload?.DepartmentList ||
        commonPayload?.departmentList ||
        [];
      const match = (Array.isArray(deptRows) ? deptRows : []).find(
        (row) =>
          String(row?.DepartmentId ?? row?.departmentId ?? "")
            .trim()
            .toLowerCase() === departmentRaw.toLowerCase(),
      );
      const name = String(
        match?.DepartmentName ?? match?.departmentName ?? "",
      ).trim();
      if (name && !looksLikeGuid(name)) departmentDisplay = name;
    } catch {
      /* keep GUID hidden rather than showing raw id */
    }
  }

  const department = departmentId || departmentRaw;

  let mappedSiteIds = parseMappedSiteIds(
    pick(info, "MappedSiteIds", "mappedSiteIds"),
  );
  if (!mappedSiteIds.length) {
    const siteExtra =
      pick(info, "SiteID", "SiteId", "siteId") ||
      studyRow?.SiteID ||
      studyRow?.SiteId ||
      "";
    if (siteExtra) mappedSiteIds = [String(siteExtra)];
  }

  const siteList = mappedSiteIds.map((siteId) => ({
    SiteId: idUpper(siteId),
    SiteName: "",
  }));

  let staffById = new Map();
  try {
    const staffRows = await getInternalActiveStaffLookup(userData);
    (staffRows || []).forEach((staff) => {
      const id = String(staff?.StaffId || staff?.staffId || "").trim().toLowerCase();
      if (id) staffById.set(id, staff);
    });
  } catch {
    staffById = new Map();
  }

  const resolveStaffDisplayName = (row, userId) => {
    const fromRow = String(
      pick(
        row,
        "StaffName",
        "staffName",
        "UserName",
        "userName",
        "Name",
        "name",
      ) || "",
    ).trim();
    if (fromRow && !looksLikeGuid(fromRow)) return fromRow;

    const firstLast = [
      pick(row, "FirstName", "firstName"),
      pick(row, "LastName", "lastName"),
    ]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(" ");
    if (firstLast) return firstLast;

    const staff = staffById.get(String(userId || "").toLowerCase());
    if (staff) {
      const fromLookup =
        String(staff.StaffName || "").trim() ||
        `${staff.FirstName || ""} ${staff.LastName || ""}`.trim();
      if (fromLookup) return fromLookup;
      const email = String(staff.EmailId || "").trim();
      if (email) return email;
    }

    const email = String(pick(row, "EmailId", "emailId", "Email") || "").trim();
    return email || "";
  };

  const userRowsRaw =
    pick(data, "StudyUsers", "studyUsers", "UserList", "userList") ||
    pick(info, "StudyUsers", "studyUsers", "UserList", "userList") ||
    [];
  const userRows = Array.isArray(userRowsRaw) ? userRowsRaw : [];
  const userList = userRows
    .map((u) => {
      const userId = idUpper(
        pick(u, "UserId", "userId", "StaffId", "staffId"),
      );
      if (!userId) return null;
      const assigned = u?.IsAssigned ?? u?.isAssigned;
      if (assigned === false || assigned === 0 || assigned === "0") return null;
      const userName = resolveStaffDisplayName(u, userId);
      if (!userName) return null;
      return {
        UserId: userId,
        UserName: userName,
        EmailId: String(pick(u, "EmailId", "emailId", "Email") || ""),
        IsAssigned: true,
      };
    })
    .filter(Boolean);

  if (pi && !userList.some((u) => String(u.UserId).toLowerCase() === pi.toLowerCase())) {
    const piName =
      String(pick(info, "PIName", "pIName", "piName") || "").trim() ||
      resolveStaffDisplayName({}, pi);
    if (piName) {
      userList.push({
        UserId: pi,
        UserName: piName,
        EmailId: "",
        IsAssigned: true,
      });
    }
  }

  const studyNumberDisplay =
    String(
      pick(info, "StudyNumber", "studyNumber") ??
        studyRow?.StudyNumber ??
        studyRow?.StudyCode ??
        studyNum ??
        "",
    ).trim() || studyNum;

  const summary = {
    studyId: resolvedStudyId,
    studyName,
    studyNumber: studyNumberDisplay,
    studyNumberDigits: studyNum,
    status: getStudyStatusLabel(info) || getStudyStatusLabel(studyRow) || STUDY_STATUS_LIVE,
    piName:
      pick(info, "PIName", "pIName", "piName") ||
      studyRow?.PIName ||
      studyRow?.PrincipalInvestigatorName ||
      resolveStaffDisplayName({}, pi) ||
      "",
    sponsorName: pick(info, "SponsorName", "sponsorName") || "",
    irasNumber: pick(info, "IRASNumber", "irasNumber") || "",
    eudractNumber: pick(info, "EudractNumber", "eudractNumber") || "",
    rdNumber: pick(info, "RandNumber", "randNumber", "RDNumber") || "",
    department: departmentDisplay || (looksLikeGuid(department) ? "" : department),
    studyColor,
    coordinatorName:
      pick(info, "CordinatorName", "cordinatorName", "CoordinatorName") || "",
    coordinatorEmail:
      pick(info, "CordinatorEmail", "cordinatorEmail", "CoordinatorEmail") || "",
    coordinatorPhone:
      pick(
        info,
        "CordinatorPhone",
        "cordinatorPhone",
        "CordinatorContact",
        "CoordinatorPhone",
      ) || "",
    siteCount: siteList.length,
    sites: siteList,
    staffCount: userList.length,
    staff: userList,
    groupType: pick(info, "GroupType", "groupType") || studyRow?.GroupType || "",
    scheduleId: getStudyScheduleId(studyRow) || "",
    startDate: pick(info, "StartDate", "startDate") || "",
    endDate: pick(info, "EndDate", "endDate") || "",
    studyType: pick(info, "StudyType", "studyType") || "",
    protocolNumber: pick(info, "ProtocolNumber", "protocolNumber") || "",
    protocolVersion: pick(info, "ProtocolVersion", "protocolVersion") || "",
    studyDescription: pick(info, "StudyDescription", "studyDescription") || "",
    phaseCode: "",
    phaseLabel: "",
  };

  const archivePayload = {
    OrganizationId: idUpper(userData.OrganizationId),
    CreatedBy: idUpper(userData.StaffId),
    StudyId: idUpper(resolvedStudyId),
    Action: "Update",
    Study: String(studyName || "").trim(),
    StudyNum: studyNum,
    PI: pi,
    EudractNumber: String(summary.eudractNumber || "").trim(),
    Randnumber: String(summary.rdNumber || "").trim(),
    // Keep existing primary site; do NOT send SiteList on archive — the SP
    // re-validates PI/staff ↔ site mapping when SiteList is present and can
    // return Success=0 (or skip the status update) even though archive only
    // needs Status='Archived'.
    SiteID:
      siteList[0]?.SiteId ||
      idUpper(
        pick(info, "SiteID", "SiteId", "siteId") ||
          studyRow?.SiteID ||
          studyRow?.SiteId ||
          "",
      ),
    Department: department || null,
    StudyColor: studyColor,
    Status: STUDY_STATUS_ARCHIVED,
    UserList: userList.length
      ? userList.map((u) => ({
          UserId: u.UserId,
          UserName: u.UserName,
          EmailId: u.EmailId || "",
          IsAssigned: "1",
        }))
      : [{ UserId: pi, UserName: summary.piName || "PI", EmailId: "", IsAssigned: "1" }],
    RemovedUserList: [],
    StudyLocationList: [],
    ProjectId: "",
    SponsorName: String(summary.sponsorName || "").trim(),
    IRASNumber: String(summary.irasNumber || "").trim(),
    CordinatorName: String(summary.coordinatorName || "").trim(),
    CordinatorPhone: String(summary.coordinatorPhone || "").trim(),
    CordinatorEmail: String(summary.coordinatorEmail || "").trim(),
    StartDate: summary.startDate || "",
    EndDate: summary.endDate || "",
    StudyType: summary.studyType || "",
    ProtocolNumber: summary.protocolNumber || "",
    ProtocolVersion: summary.protocolVersion || "",
    StudyDescription: summary.studyDescription || "",
  };

  return {
    response,
    info,
    summary,
    archivePayload,
  };
}

/**
 * Full archive readiness: eISF pending, ePSF pending, confirmed appointments, study summary.
 */
export async function fetchStudyArchiveReadiness({
  userData,
  studyRow,
  siteIds = null,
}) {
  const studyId = getStudyRowId(studyRow);
  let studyScheduleId =
    getStudyScheduleId(studyRow) ||
    String(studyRow?.StudyScheduleId ?? studyRow?.studyScheduleId ?? "").trim();

  const [studyBundle, isf, psf, appointments, retentionPeriodYears] =
    await Promise.all([
      loadStudyForArchive({ userData, studyId, studyRow }),
      fetchIsfPsfPendingBundle({
        userData,
        studyId,
        siteIds,
        templateCategory: "eISF",
      }),
      fetchIsfPsfPendingBundle({
        userData,
        studyId,
        siteIds,
        templateCategory: "ePSF",
      }),
      studyScheduleId
        ? fetchConfirmedAppointmentsForStudy({
            userData,
            studyScheduleId,
          }).catch(() => ({ appointments: [], count: 0 }))
        : Promise.resolve({ appointments: [], count: 0, noSchedule: true }),
      fetchOrgArchiveRetentionYears(userData),
    ]);

  if (!studyScheduleId) {
    studyScheduleId = String(studyBundle?.summary?.scheduleId || "").trim();
  }

  // Re-check appointments if schedule id was only known after study load
  let appointmentsResult = appointments;
  if (studyScheduleId && appointments?.noSchedule) {
    appointmentsResult = await fetchConfirmedAppointmentsForStudy({
      userData,
      studyScheduleId,
    }).catch(() => appointments);
  }

  const appointmentBlockers = appointmentsResult.appointments || [];
  const isfClear = Boolean(isf?.isClear);
  const psfClear = Boolean(psf?.isClear);
  const appointmentsClear = appointmentBlockers.length === 0;
  const canArchive = isfClear && psfClear && appointmentsClear;

  const phaseCode = isf?.gateCode || psf?.gateCode || "PRE_STUDY";
  const phaseLabel =
    isf?.gateLabel || psf?.gateLabel || gateNameForCode(phaseCode);

  const summary = {
    ...studyBundle.summary,
    phaseCode,
    phaseLabel,
  };

  const blockers = [];
  if (!isfClear) {
    blockers.push({
      key: "eisf",
      label: "eISF has pending uploads or signatures",
      count: isf.pendingCount,
    });
  }
  if (!psfClear) {
    blockers.push({
      key: "epsf",
      label: "ePSF has pending uploads or signatures",
      count: psf.pendingCount,
    });
  }
  if (!appointmentsClear) {
    blockers.push({
      key: "appointments",
      label: "Confirmed appointments must be cancelled first",
      count: appointmentBlockers.length,
    });
  }

  return {
    studyId,
    studyScheduleId,
    summary,
    archivePayload: studyBundle.archivePayload,
    retentionPeriodYears:
      retentionPeriodYears || DEFAULT_ARCHIVE_RETENTION_YEARS,
    isf,
    psf,
    appointments: {
      ...appointmentsResult,
      isClear: appointmentsClear,
    },
    canArchive,
    blockers,
  };
}

export async function archiveStudyWithPayload(archivePayload, archiveReason = "") {
  if (!archivePayload?.StudyId) {
    throw new Error("Missing study payload for archive.");
  }
  if (!archivePayload?.StudyNum) {
    throw new Error("Missing study number for archive.");
  }
  if (!archivePayload?.PI) {
    throw new Error("Missing Principal Investigator for archive.");
  }

  const payload = {
    ...archivePayload,
    Status: STUDY_STATUS_ARCHIVED,
    Action: "Update",
    ArchiveReason: String(archiveReason || "").trim(),
  };
  // Never re-submit SiteList on archive (see loadStudyForArchive notes).
  delete payload.SiteList;

  const response = await apiClient.post(
    API_ENDPOINTS.STUDY.STUDY_ACTIVITY,
    payload,
  );

  const apiOk =
    Number(response?.success) === 1 || Number(response?.Success) === 1;
  if (!apiOk) {
    return response;
  }

  // SP can return Success=1 even when StudyNumber did not match and Status
  // was not written — verify before telling the user it worked.
  try {
    const verifyRes = await apiClient.post(API_ENDPOINTS.STUDY.STUDY_ACTIVITY, {
      OrganizationId: payload.OrganizationId,
      CreatedBy: payload.CreatedBy,
      StaffIdRef: payload.CreatedBy,
      StudyId: payload.StudyId,
      Action: "GetStudyInfo",
    });
    const verifyData = unwrapActivityPayload(verifyRes);
    const verifyInfo = getFirstStudyInfo(verifyData) || {};
    const status = String(
      pick(verifyInfo, "StudyStatus", "studyStatus", "Status") || "",
    ).trim();
    const inactive = verifyInfo?.InActive ?? verifyInfo?.inActive;
    const archived =
      /^archiv/i.test(status) ||
      inactive === 1 ||
      inactive === true ||
      verifyInfo?.Archive === 1 ||
      verifyInfo?.Archive === true;

    if (!archived) {
      return {
        success: 0,
        Success: 0,
        message:
          response?.message ||
          "The archive request completed, but the study status was not changed. Check study number and try again.",
        data: response?.data,
      };
    }
  } catch {
    /* verification is best-effort; keep original success if re-fetch fails */
  }

  return response;
}

export function isStudyAlreadyArchived(studyRow) {
  const status = getStudyStatusLabel(studyRow).toLowerCase();
  return (
    status === "archived" ||
    status === "archive" ||
    studyRow?.InActive === 1 ||
    studyRow?.InActive === true ||
    studyRow?.Archive === 1 ||
    studyRow?.Archive === true
  );
}
